import { useCallback } from 'react';
import { CONTENT_PLATFORM_PROPERTY_NAME } from 'content-management-data-lib';
import { getEarlyRequesterResultPromise } from '../utils/getEarlyRequesterResultPromise';
import { getMergedContentGroupAssociationsFilterValues } from '../utils/crmSearchRequestOptionsUtils';
import { isInOrNotInOperator } from '../utils/typeCheckUtils';
import { getRequiredFiltersForEmptyState } from '../utils/crmEmptyStateRequestOptionsUtils';
import useContentGroups from './useContentGroups';
import { registerQuery, useDataFetchingClient, useQuery } from 'data-fetching-client';
import http from 'hub-http/clients/apiClient';
const fetchContentForEmptyStateWithoutEarlyRequest = ({
  contentObjectType,
  emptyStateFilter,
  mergedContentGroupAssociations = {}
}) => {
  const correctedEmptyStateFilters = emptyStateFilter === null || emptyStateFilter === void 0 ? void 0 : emptyStateFilter.filterGroups[0].filters.map(filter => {
    if (filter.property === CONTENT_PLATFORM_PROPERTY_NAME.GROUP_ID && isInOrNotInOperator(filter)) {
      return getMergedContentGroupAssociationsFilterValues(filter, mergedContentGroupAssociations);
    }
    return filter;
  });
  return http.post(`content-management/search/v1/${contentObjectType}`, {
    data: {
      count: 1,
      filterGroups: [{
        filters: [...(correctedEmptyStateFilters || []), ...getRequiredFiltersForEmptyState()]
      }],
      sorts: [{
        order: 'DESC',
        property: CONTENT_PLATFORM_PROPERTY_NAME.CREATED_AT
      }],
      objectTypeId: '0-10',
      offset: 0,
      query: '',
      requestOptions: {
        properties: ['hs_object_id']
      }
    }
  });
};
export const fetchContentForEmptyState = ({
  contentObjectType,
  emptyStateFilter,
  mergedContentGroupAssociations = {}
}) => {
  return getEarlyRequesterResultPromise({
    name: 'fetch-empty-state-content-objects',
    retryRequest: () => fetchContentForEmptyStateWithoutEarlyRequest({
      contentObjectType,
      emptyStateFilter,
      mergedContentGroupAssociations
    })
  });
};
const EMPTY_STATE_DATA = registerQuery({
  fieldName: 'emptyStateData',
  args: ['contentObjectType', 'emptyStateFilter', 'mergedContentGroupAssociations'],
  fetcher: fetchContentForEmptyState
});
export const useQueryCacheForEmptyStateData = ({
  contentObjectType,
  contentGroupObjectType,
  emptyStateFilter
}) => {
  const {
    mergedContentGroupAssociations
  } = useContentGroups(contentGroupObjectType);
  const client = useDataFetchingClient();
  const queryCacheForEmptyStateData = useCallback(() => {
    return client.query({
      query: EMPTY_STATE_DATA,
      variables: {
        contentObjectType,
        emptyStateFilter,
        mergedContentGroupAssociations
      }
    });
  }, [client, contentObjectType, emptyStateFilter, mergedContentGroupAssociations]);
  return {
    queryCacheForEmptyStateData
  };
};

// Fetches the content data needed to determine empty state on load
// It supports content types without Groups and with Groups based on the `contentGroupObjectType`
// This hook will wait for the Content Groups to load and, if appropriate, wait for
// `emptyStateFilter` to be defined before fetching the content for empty state data.
// This is because the `emptyStateFilter` may be dynamic and depend on other data loaded first.
export const useContentEmptyStateData = ({
  contentObjectType,
  contentGroupObjectType,
  emptyStateFilter,
  shouldWaitForEmptyStateFilter = false
}) => {
  const {
    contentGroupsLoaded,
    mergedContentGroupAssociations
  } = useContentGroups(contentGroupObjectType);
  const {
    data,
    loading,
    error
  } = useQuery(EMPTY_STATE_DATA, {
    skip: !contentGroupsLoaded || shouldWaitForEmptyStateFilter && !emptyStateFilter,
    variables: {
      contentObjectType,
      emptyStateFilter,
      mergedContentGroupAssociations
    }
  });
  return {
    emptyStateData: data === null || data === void 0 ? void 0 : data.emptyStateData,
    emptyStateTotal: data === null || data === void 0 ? void 0 : data.emptyStateData.total,
    isEmptyStateDataLoading: loading,
    emptyStateDataError: error
  };
};